import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { firestoreConnect } from 'react-redux-firebase';
import M from 'materialize-css';
import { Redirect } from 'react-router-dom';
import ModalCompanyDialog from './ModalCompanyDialog';
import { countryCodes } from '../../config/keys';
import { badgesType } from '../../constants/constants';
import Functions from '../../api/Functions';
import parse from 'csv-parse';
import ImportExample from '../../resources/ImportExample.csv';
import UpdateExample from '../../resources/UpdateExample.csv';
import { todayToYYYYMMDD } from '../../utils/DateUtil';

const refModal = React.createRef();
const safetyPynMessageDefault = 'Safety Zones help to promote safe driving behaviours in areas that need extra attention from drivers. Help keep our communities safe by slowing down and scanning for pedestrians or cyclists.';
const descriptions = {
  foodZone: 'Drive safely near Food Zone Pyns to earn rewards! When you’re out grabbing food, make sure to watch for pedestrians while you navigate the parking lot or head to the drive-thru!',
  driveZone: 'Drive safely near Drive Zone Pyns to earn rewards! Remember to drive with caution as you approach gas station pumps or service bays as there may be hazards or moving pedestrians in these areas.',
  shopZone: 'Drive safely near Shop Zone Pyns to earn rewards! Be cautious as you navigate shopping centre parking lots and keep an eye out for children or shopping carts that may appear suddenly.',
};

function ImportTrypStops(props) {
  const { auth, companies, trypStopCompanies = [] } = props;
  const [pynType, setPynType] = useState(null);
  const [pynsFromFile, setPynsFromFile] = useState([]);
  const [trypStopCompany, setTrypStopCompany] = useState(null);
  const [trypStopDescription, setTrypStopDescription] = useState(null);
  const [currentCompanies, setCurrentCompanies] = useState([]);
  const [country, setCountry] = useState('');
  const [companiesSelect, setCompaniesSelect] = useState({});
  const [showProcessText, setShowProcessText] = useState(false);
  const [filename, setFileName] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [isNewImport, setIsNewImport] = useState(true);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      parse(bstr, { columns: true }, (err, output) => {
        const uniquePynsFromFile = removeDuplicates(output);
        setPynsFromFile(organizeInBatches(uniquePynsFromFile));
      });
    };
    reader.readAsBinaryString(file);
  };

  const organizeInBatches = (array) => {
    const res = [];
    while (array.length) res.push(array.splice(0, 5000));
    return res;
  };

  const removeDuplicates = (array) => {
    const info = { total: array.length };
    let uniquePyns = isNewImport ? getPynsAfterCheckingDuplicatedFields(array) : array;
    setFileInfo({ ...info, notRepeated: uniquePyns.length });
    return uniquePyns;
  };

  const getPynsAfterCheckingDuplicatedFields = (array) => {
    return array.reduce((unique, o) => {
      if (!unique.some((obj) => obj.store_number === o.store_number && obj.latitude === o.latitude && obj.longitude === o.longitude)) {
        unique.push(o);
      }
      return unique;
    }, []);
  };

  useEffect(() => M.AutoInit(), []);

  useEffect(() => {
    let elems = document.querySelectorAll('select');
    M.FormSelect.init(elems, {});
  }, [trypStopCompanies, pynType, isNewImport]);

  useEffect(() => {
    setPynsFromFile([]);
    cleanInputs();
  }, [isNewImport]);

  const openCompanyDialog = (country) => {
    updateCountry(country);
    const modal = M.Modal.getInstance(refModal.current);
    modal.open();
  };

  const filterCompanies = (country, companyName) => {
    if (!companies) return;
    let filteredCompanies = companies.filter((company) => {
      return company.countryCode === country && (companyName !== '' ? company.companyName.toLowerCase().includes(companyName.toLowerCase()) : true);
    });
    setCurrentCompanies(filteredCompanies);
  };

  const search = (searchTerm) => filterCompanies(country, searchTerm);

  const updateCountry = (country) => {
    setCountry(country);
    filterCompanies(country, '');
  };

  const validFields = () => {
    const validPynsFromFile = pynsFromFile.length > 0;
    const validTrypStopCompany = pynType !== badgesType.TRYP_STOPS || trypStopCompany !== null;
    let validCompanies = Object.keys(companiesSelect).length > 0;
    for (let key in companiesSelect) {
      if (Object.keys(companiesSelect[key]).length === 0) {
        validCompanies = false;
        return;
      }
    }
    const valid = validPynsFromFile && validTrypStopCompany && validCompanies;
    if (!valid) M.toast({ html: `Missing fields` });
    return valid;
  };

  const getPynsListByType = (pyns) => {
    switch (pynType) {
      case badgesType.TRYP_STOPS:
        return getTrypStopsList(pyns);
      case badgesType.SAFETY:
        return getSafetyList(pyns);
      default:
        return getCompanyPynsList(pyns);
    }
  };

  const getTrypStopsList = (pyns) => pyns.map((p) => getNewTrypStop(p));
  const getSafetyList = (pyns) => pyns.map((p) => getNewSafety(p));
  const getCompanyPynsList = (pyns) => pyns.map((p) => getNewCompanyPyn(p));

  const getNewTrypStop = (pyn) => {
    const company = companiesSelect[pyn.country_code];
    return {
      ...pyn,
      uidCompany: company.uid,
      name: pyn.store_name || '',
      idNonVendorCompany: trypStopCompany.uid,
      type: badgesType.TRYP_STOPS,
      country: pyn.country_code,
      importedFrom: `${filename}/${todayToYYYYMMDD()}`,
      tlGeoFencesSet: false,
      latitude: Number(pyn.latitude),
      longitude: Number(pyn.longitude),
      display: {
        image_card: trypStopCompany.circleIconUrl,
        title: trypStopCompany.companyName,
        description: descriptions[trypStopDescription],
        distance: false,
        button_directions: false,
        icon_pyn_light: 'blue',
        icon_pyn_dark: 'blue',
        address: pyn.address,
      },
    };
  };

  const getNewSafety = (pyn) => {
    const company = companiesSelect[pyn.country_code];
    return {
      ...pyn,
      uidCompany: company.uid,
      name: pyn.store_name || '',
      type: badgesType.SAFETY,
      country: pyn.country_code,
      importedFrom: `${filename}-${todayToYYYYMMDD()}`,
      tlGeoFencesSet: false,
      latitude: Number(pyn.latitude),
      longitude: Number(pyn.longitude),
      display: {
        image_card: company.circleIconUrl,
        title: company.companyName,
        description: company.safetyPynMessage || safetyPynMessageDefault,
        distance: false,
        button_directions: false,
        icon_pyn_light: 'black',
        icon_pyn_dark: 'white',
        address: pyn.address,
      },
    };
  };

  const getNewCompanyPyn = (pyn) => {
    const company = companiesSelect[pyn.country_code];
    return {
      ...pyn,
      uidCompany: company.uid,
      name: pyn.store_name || '',
      type: badgesType.COMPANY,
      country: pyn.country_code,
      importedFrom: `${filename}-${todayToYYYYMMDD()}`,
      tlGeoFencesSet: false,
      latitude: Number(pyn.latitude),
      longitude: Number(pyn.longitude),
      display: {
        image_card: company.circleIconUrl,
        title: company.companyName,
        description: `${pyn.store_name || company.companyName}\n${pyn.address}`,
        distance: false,
        button_directions: false,
        icon_pyn_light: 'blue',
        icon_pyn_dark: 'blue',
        address: pyn.address,
      },
    };
  };

  const importPynsFromFile = async () => {
    if (showProcessText || pynsFromFile.length === 0 || !validFields()) return;
    setShowProcessText(true);
    const pynsList = getPynsListByType(pynsFromFile[0]);
    importPyns(pynsList);
  };

  const importPyns = (pynsList) => {
    Functions.importPynsFromFile(pynsList)
      .then(() => batchImportedSuccesfully())
      .catch((e) => M.toast({ html: `Error: ${e.message}` }))
      .finally(() => setShowProcessText(false));
  };

  const updatePynsFromFile = async () => {
    if (showProcessText || pynsFromFile.length === 0) return;
    setShowProcessText(true);
    formatFields(pynsFromFile[0]);
    updatePyns(pynsFromFile[0]);
  };

  const updatePyns = (pynsList) => {
    Functions.updatePynsFromFile(pynsList)
      .then(() => batchImportedSuccesfully())
      .catch((e) => M.toast({ html: `Error: ${e.message}` }))
      .finally(() => setShowProcessText(false));
  };

  const batchImportedSuccesfully = () => {
    const newPyns = [...pynsFromFile];
    newPyns.shift();
    setPynsFromFile(newPyns);
    M.toast({ html: `${newPyns.length === 0 ? 'Finished importing' : 'Batch imported'}` });
    if (newPyns.length === 0) cleanInputs();
  };

  const formatFields = (pyns) => {
    pyns.forEach((pyn) => formatPynFields(pyn));
  };

  const formatPynFields = (pyn) => {
    parseLocationFields(pyn);
    Object.keys(pyn).forEach((pynField) => {
      if (!pyn[pynField]) {
        removeEmptyField(pyn, pynField);
      } else if (pynField.startsWith('display_')) {
        mapDisplayField(pyn, pynField);
      }
    });
  };

  const parseLocationFields = (pyn) => {
    if (pyn.latitude) {
      pyn.latitude = Number(pyn.latitude);
    }
    if (pyn.longitude) {
      pyn.longitude = Number(pyn.longitude);
    }
  };

  const removeEmptyField = (pyn, key) => delete pyn[key];

  const mapDisplayField = (pyn, field) => {
    const newField = field.replace('display_', '');
    if (pyn.display) {
      pyn.display[newField] = pyn[field];
    } else {
      pyn.display = { [newField]: pyn[field] };
    }
    delete pyn[field];
  };

  const cleanInputs = () => {
    setPynType(null);
    setTrypStopCompany(null);
    setCountry('');
    setCompaniesSelect({});
    setFileName(null);
    setFileInfo(null);
  };

  const setCompany = (company) => setCompaniesSelect({ ...companiesSelect, [country]: company });

  const renderIsNewImport = () => (
    <div className="row">
      <div className="input-field col s6">
        <label>
          <input name="group1" type="radio" checked={isNewImport} onChange={() => setIsNewImport(true)} />
          <span>New import</span>
        </label>
      </div>
      <div className="input-field col s6">
        <label>
          <input name="group2" type="radio" checked={!isNewImport} onChange={() => setIsNewImport(false)} />
          <span>Update</span>
        </label>
      </div>
    </div>
  );

  const renderNewImportFields = () => (
    <div>
      {renderPynTypeSelect()}
      {pynType === badgesType.TRYP_STOPS && (
        <div>
          {renderTrypStopSelect()}
          {renderTrypStopDescriptionSelect()}
        </div>
      )}
      {renderCompaniesSelect()}
      {renderNewImportInstructions()}
      {renderCSVInput()}
      <br />
      {renderCallButton('Import Pyns from CSV', importPynsFromFile)}
    </div>
  );

  const renderPynTypeSelect = () => {
    return (
      <div className="row">
        <div className="input-field">
          <select value={pynType || ''} onChange={(e) => setPynType(e.target.value)}>
            <option value={null}>None</option>
            {Object.keys(badgesType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <label>Pyn Type</label>
        </div>
      </div>
    );
  };

  const renderTrypStopSelect = () => {
    if (!trypStopCompanies) return null;
    return (
      <div className="input-field row">
        <select value={trypStopCompany ? trypStopCompany.id || '' : ''} onChange={(e) => setTrypStopCompany(trypStopCompanies[e.target.value])}>
          <option value="">Select a Tryp Stop Company</option>
          {Object.keys(trypStopCompanies).map((key) => (
            <option key={key} value={key}>
              {trypStopCompanies[key].companyName}
            </option>
          ))}
        </select>
        <label>Tryp Stop Company</label>
      </div>
    );
  };

  const renderTrypStopDescriptionSelect = () => {
    return (
      <div className="input-field row">
        <select value={trypStopDescription || ''} onChange={(e) => setTrypStopDescription(e.target.value)}>
          <option value="">Select a Tryp Stop Description</option>
          {Object.keys(descriptions).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
        <label>Tryp Stop Description</label>
      </div>
    );
  };

  const renderCompaniesSelect = () => (
    <div>
      {renderCountrySelect()}
      {renderCompaniesInputs()}
    </div>
  );

  const renderCountrySelect = () => (
    <div className="input-field row">
      <select
        value=""
        onChange={(e) => {
          const countryCode = e.target.value;
          if (!companiesSelect[countryCode]) setCompaniesSelect({ ...companiesSelect, [countryCode]: {} });
        }}>
        <option value="">Add country code</option>
        {Object.keys(countryCodes).map((country) => (
          <option key={country} value={country}>
            {country}
          </option>
        ))}
      </select>
      <label>Company by country</label>
    </div>
  );

  const renderCompaniesInputs = () => (
    <div className="row">
      {Object.keys(companiesSelect).map((key) => {
        return (
          <div className="row input-field" key={key} style={{ verticalAlign: 'center' }}>
            <p className="col s1">{key}</p>
            <div className="col s11" onClick={() => openCompanyDialog(key)}>
              <input type="text" value={companiesSelect[key].uid || ''} onChange={() => { }} />
              {companiesSelect[key].companyName && <small>{companiesSelect[key].companyName}</small>}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderNewImportInstructions = () => (
    <div>
      <h6 style={{ marginRight: 20 }}>Select file:</h6>
      <p style={{ fontWeight: 'bold' }}>(NEW PYN IDS COULD BE GENERATED)</p>
      <p>1. Make sure all headers are written in snake case (lowercase letters separated by underscores).</p>
      <p>2. Obligatory fields:</p>
      <ol>
        <li>store_number</li>
        <li>store_name</li>
        <li>address</li>
        <li>latitude</li>
        <li>longitude</li>
        <li>state</li>
        <li>country_code</li>
      </ol>
      <a className="btn" href={ImportExample} download="ImportExample.csv">
        Download File Example
      </a>
    </div>
  );

  const renderCSVInput = () => (
    <div>
      <input type="file" id="csv_input" accept=".csv" onChange={handleFileUpload} style={{ marginTop: 20, marginBottom: 20 }} />
      {renderFileInfo()}
    </div>
  );

  const renderFileInfo = () =>
    fileInfo && (
      <div className="row">
        <p>Total pyns from file: {fileInfo.total}</p>
        <p>Total after removing duplicates: {fileInfo.notRepeated}</p>
      </div>
    );

  const renderCallButton = (label, onClick) => (
    <div className="row">
      <button className="col s3 waves-effect waves-light btn-small" onClick={onClick}>
        {label}
      </button>
      <div className="col s2">Batches to import: {pynsFromFile.length}</div>
      <div className="col s5">{showProcessText && <div>Processing...</div>}</div>
    </div>
  );

  const renderUpdateFields = () => (
    <div>
      {renderUpdateInstructions()}
      {renderCSVInput()}
      <br />
      {renderCallButton('Update Pyns from CSV', updatePynsFromFile)}
    </div>
  );

  const renderUpdateInstructions = () => (
    <div>
      <h6 style={{ marginRight: 20 }}>Select file:</h6>
      <p style={{ fontWeight: 'bold' }}>(Pyns with the id column will be updated)</p>
      <p>1. Obligatory fields:</p>
      <ol>
        <li>id</li>
      </ol>
      <p>2. Display field notes:</p>
      <p>To change fields from the display, name the headers with the prefix "display_"</p>
      <p>Example: display_address</p>
      <p>3. Fields left blank will not be included in the update</p>
      <a className="btn" href={UpdateExample} download="UpdateExample.csv">
        Download File Example
      </a>
    </div>
  );

  const renderAllPynsFromFile = () => <div>{pynsFromFile && pynsFromFile.length > 0 ? pynsFromFile.map((pyns) => renderPynsFromFile(pyns)) : <div />}</div>;

  const renderPynsFromFile = (pyns) => (pyns && pyns.length > 0 ? pyns.map((pyn) => renderPynFromFile(pyn)) : null);

  const renderPynFromFile = (pyn) => {
    const pynFields = Object.keys(pyn);
    const showFields = pynFields.length >= 4 ? pynFields.slice(0, 4) : pynFields;
    return (
      <div className="card" key={pyn.id || pyn['store_number']}>
        <div className="card-content">
          {showFields.map((field) => (
            <p key={field}>{`${field}: ${pyn[field]}`}</p>
          ))}
        </div>
      </div>
    );
  };

  if (!auth.uid) return <Redirect to="/signin" />;
  return (
    <div className="container">
      <ModalCompanyDialog refModal={refModal} search={search} currentCompanies={currentCompanies} setUidCompany={() => { }} setCompany={setCompany} />
      <div className="title-section">Import Pyns from File</div>
      {renderIsNewImport()}
      <br />
      {isNewImport ? renderNewImportFields() : renderUpdateFields()}
      {renderAllPynsFromFile()}
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    companies: state.firestore.ordered.companies,
    trypStopCompanies: state.firestore.data['trypStopCompanies'],
    auth: state.firebase.auth,
  };
};

export default compose(
  firestoreConnect([
    { collection: 'companies', orderBy: ['companyName', 'asc'] },
    { collection: 'companies', where: ['type', '==', 'TRYP_STOPS'], storeAs: 'trypStopCompanies' },
  ]),
  connect(mapStateToProps),
)(ImportTrypStops);
